/**
 * The "signed in, but the system doesn't know you" screen.
 *
 * This page exists because of a specific, reachable failure: public.users rows
 * are not created automatically (Decision #35), so a valid Supabase login can
 * exist with no Attest profile behind it. Row-level security then returns
 * zero rows for everything — WITHOUT an error. Without this page, that person
 * sees an empty dashboard and reasonably concludes the software is broken.
 *
 * The user id is shown as selectable text on purpose: it is the one piece of
 * information whoever fixes this actually needs.
 */
import { Navigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth.js';

export function NoProfilePage() {
  const { user, authStatus, profileStatus, signOut } = useAuth();
  const inactive = profileStatus === 'inactive';

  // This route sits outside ProtectedRoute (which would bounce back here and
  // loop), so it has to turn signed-out visitors away itself.
  if (authStatus === 'signed-out') return <Navigate to="/login" replace />;
  if (authStatus === 'loading') {
    return (
      <div className="centered-page" aria-busy="true">
        <p className="muted">Checking your sign-in…</p>
      </div>
    );
  }

  return (
    <div className="centered-page">
      <div className="card">
        <h1>{inactive ? 'This account is switched off' : 'Your account isn’t set up yet'}</h1>

        {inactive ? (
          <p>
            Your sign-in worked, but this account has been deactivated, so it
            can’t see any training records. If that’s unexpected, ask your
            training coordinator to reactivate it.
          </p>
        ) : (
          <p>
            Your sign-in worked — but no staff profile has been created for you
            yet, so there’s nothing for you to see. Someone with administrator
            access needs to finish setting you up.
          </p>
        )}

        <p className="muted">Give whoever helps you these details:</p>
        <dl className="detail-list">
          <div>
            <dt>Email</dt>
            <dd>
              <code>{user?.email}</code>
            </dd>
          </div>
          <div>
            <dt>Account ID</dt>
            <dd>
              <code>{user?.id}</code>
            </dd>
          </div>
        </dl>

        <button type="button" className="btn" onClick={signOut}>
          Sign out
        </button>
      </div>
    </div>
  );
}

export default NoProfilePage;
